import { addDays, format, max, startOfMonth } from 'date-fns';
import { useEffect, useState } from 'react';
import { useIsMounted } from 'usehooks-ts';

import type { SearchType } from '@lt/api/dist/types/searchParams';
import { useGetApiClient } from '../../../context/apiContext';
import { calcMinDate } from '../components/DatePicker/utils';

interface useFetchIndicationParams {
  isMobile: boolean;
  searchType: SearchType;
  departureId?: number;
  placeId?: number;
  calendarOpened: boolean;
  startDate: Date | null;
}

export const useFetchIndication = ({
  isMobile,
  searchType,
  departureId,
  placeId,
  calendarOpened,
  startDate,
}: useFetchIndicationParams) => {
  const api = useGetApiClient();

  const [indicationMap, setIndicationMap] = useState<Readonly<Map<string, boolean>>>(
    new Map<string, boolean>()
  );
  const [startCurrentMonth, setStartCurrentMonth] = useState<Date>(calcMinDate(searchType));

  const isMounted = useIsMounted();

  useEffect(() => {
    if (startDate) {
      setStartCurrentMonth(startOfMonth(startDate));
    } else {
      setStartCurrentMonth(startOfMonth(calcMinDate(searchType)));
    }
  }, [calendarOpened, searchType, startDate]);

  useEffect(() => {
    if (!api || !placeId || !departureId) return;
    const fetchDates = async () => {
      const params = {
        start_date: format(max([startCurrentMonth, addDays(new Date(), 1)]), 'yyyy-MM-dd'),
        range: isMobile ? 0 : 1,
        search_type: searchType,
        place_id: placeId,
        departure_id: departureId,
      };

      const response = await api.getReferencesSearchCalendarStartDate(params);

      if (isMounted()) {
        setIndicationMap(response);
      }
    };

    fetchDates();
  }, [startCurrentMonth, placeId, departureId, searchType]);

  return { indicationMap, setStartCurrentMonth };
};
